import * as Sentry from '@sentry/browser';
import { SentryBoundary } from 'app/sentry/SentryBoundary';
import 'assets/styles/fonts.css';
import { CspNonceContext } from 'designSystem/context';
import { ModeContext } from 'context/mode/ModeContext';
import Cookies from 'js-cookie';
import React, { Fragment } from 'react';
import { createRoot } from 'react-dom/client';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { Provider } from 'react-redux';
import { DesignSystemStyleManager, GlobalStyle as BaseGlobalStyle, NotificationsContextProvider } from 'designSystem';
import { LocaleProvider } from 'shared/localeProvider/LocaleProvider';
import { createStore } from 'store/store';
import { ThemeProvider } from 'styled-components';
import { getThemeByMode } from 'theme/Theme';
import { getAppMode, getEnvironmentVar, getLocaleByDomain } from 'utils';

import { App } from './app/App';
import { GlobalStyle } from './index.styles';

if (getEnvironmentVar('REACT_APP_SENTRY_KEY') && getEnvironmentVar('REACT_APP_SENTRY_PROJECT')) {
  Sentry.init({
    dsn: `https://${getEnvironmentVar('REACT_APP_SENTRY_KEY')}@sentry.io/${getEnvironmentVar(
      'REACT_APP_SENTRY_PROJECT',
    )}`,
  });
}

const appMode = getAppMode();
const locale = getLocaleByDomain(appMode, Cookies.get('locale'));
const store = createStore({ locale, appMode });
const cspNonce = document.querySelector<HTMLMetaElement>('meta[name="csp-nonce"]')?.content;

const rootElement = document.getElementById('developer-portal');

if (!rootElement) {
  throw new Error('Missing developer portal root element');
}

createRoot(rootElement).render(
  <SentryBoundary>
    <DesignSystemStyleManager nonce={cspNonce}>
      <CspNonceContext.Provider value={cspNonce}>
        <Provider store={store}>
          <ThemeProvider theme={getThemeByMode(appMode)}>
            <ModeContext.Provider value={{ mode: appMode }}>
              <LocaleProvider locale={locale}>
                <GoogleReCaptchaProvider
                  reCaptchaKey={getEnvironmentVar('REACT_APP_RECAPTCHA_KEY') || ''}
                  scriptProps={{ nonce: cspNonce }}
                >
                  <NotificationsContextProvider>
                    <Fragment>
                      <BaseGlobalStyle />
                      <GlobalStyle />
                      <App />
                    </Fragment>
                  </NotificationsContextProvider>
                </GoogleReCaptchaProvider>
              </LocaleProvider>
            </ModeContext.Provider>
          </ThemeProvider>
        </Provider>
      </CspNonceContext.Provider>
    </DesignSystemStyleManager>
  </SentryBoundary>,
);
